import { useQuery } from '@tanstack/react-query';
import {
  type ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useDebounce } from '@revelio/core';

import { fetchCompanySearchResults } from '../../../../api/fetchCompanySearchResults';
import type { TNode } from '../../Node';
import type { TreeData } from '../../useTreeApi';
import {
  insertNodeAtPath,
  MIN_SEARCH_LENGTH,
  sortTreeDataByChildNodeCount,
} from './utils';

export interface TypeaheadFetchConfig {
  queryKeyPrefix?: string;
}

export interface FetchResults {
  sortedData: TreeData[];
  rawMappedResults: TreeData[];
}

// Extend TreeData type to include topCompanies
type ExtendedTreeData = TreeData & {
  top_companies?: string[];
};

const isCompanyNode = (id: string): boolean => {
  let underscores = 0;
  for (let i = 0; i < id.length; i++) {
    if (id[i] === '_') {
      underscores++;
      if (underscores === 2) return true;
    }
  }
  return false;
};

const fetchAndMapResults = async (
  searchTerm: string,
  initialData: TreeData[],
  signal: AbortSignal
): Promise<FetchResults> => {
  if (!searchTerm || searchTerm.trim().length < MIN_SEARCH_LENGTH) {
    return Promise.resolve({
      sortedData: initialData,
      rawMappedResults: [],
      unMappedResults: [],
    });
  }

  const rawData = await fetchCompanySearchResults(searchTerm, signal);

  const rawMappedResults = rawData.map((result) => {
    const identifiers = result.primary_identifiers || {};

    const {
      rics_k10,
      rics_k50,
      rics_k400,
      rics_k400_name,
      primary_name: name,
      primary_ticker: ticker,
      primary_website,
      primary_linkedin_url,
      city,
      country,
    } = identifiers;

    return {
      id: `${rics_k10}_${rics_k400}_${result.company_id}`,
      name,
      label: name,
      longName: name,
      shortName: name,
      rcid: result.rcid,
      nodeCount: result.raw_emp_count,
      ticker,
      industry: rics_k10,
      ricsK10Id: rics_k10,
      ricsK50Id: rics_k50,
      ricsK400Id: rics_k400,
      rics_k10,
      rics_k50,
      rics_k400,
      ultimateParentId: result.ult_parent_rcid,
      entityInfo: {
        rcid: result.rcid,
        ult_parent_name: result.ult_parent_name,
        hasUltimateParent: result.ult_parent_rcid !== result.rcid,
        primary_website,
        primary_linkedin_url,
        industry: rics_k400_name,
        city,
        country,
      },
    };
  });

  const sortedResultsWithAsyncEntities = rawMappedResults.sort(
    (a, b) => (b.nodeCount || 0) - (a.nodeCount || 0)
  );

  const updatedData = insertNodeAtPath(initialData, rawMappedResults);

  const sortedData = sortTreeDataByChildNodeCount(updatedData, searchTerm);

  return {
    sortedData,
    rawMappedResults: sortedResultsWithAsyncEntities,
  };
};

const extractCompaniesFromTreeData = (data: TreeData[]): TreeData[] => {
  const companies: TreeData[] = [];

  const traverse = (nodes: TreeData[]) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (!node) continue;

      if (isCompanyNode(node.id)) {
        companies.push(node);
      }
      if (node.children) {
        traverse(node.children);
      }
    }
  };

  traverse(data);
  return companies;
};

const extractIndustriesFromTreeData = (
  data: TreeData[]
): ExtendedTreeData[] => {
  const result: ExtendedTreeData[] = [];

  for (let i = 0; i < data.length; i++) {
    const node = data[i];
    if (!node) continue;

    if (isCompanyNode(node.id)) continue;

    const hasChildren = node.children && node.children.length > 0;

    if (!hasChildren) {
      result.push(node);
      continue;
    }

    const companies: TreeData[] = [];
    const industries: TreeData[] = [];

    for (let j = 0; j < (node?.children?.length ?? 0); j++) {
      const child = node?.children?.[j];
      if (!child) continue;

      if (isCompanyNode(child.id)) {
        companies.push(child);
      } else {
        industries.push(child);
      }
    }

    const newNode: ExtendedTreeData = { ...node };

    if (companies.length > 0) {
      companies.sort((a, b) => (b.nodeCount || 0) - (a.nodeCount || 0));

      const topCompanyNames: string[] = [];
      const limit = Math.min(5, companies.length);
      for (let k = 0; k < limit; k++) {
        const company = companies[k];
        if (company && company.name) {
          topCompanyNames.push(company.name);
        }
      }

      if (topCompanyNames.length > 0) {
        newNode.entityInfo = {
          ...node.entityInfo,
          top_companies: topCompanyNames,
        };
      }
    }

    if (industries.length > 0) {
      newNode.children = extractIndustriesFromTreeData(industries);
    } else {
      delete newNode.children;
    }

    result.push(newNode);
  }

  return result;
};

// Hook
export const useCompanySearch = (
  searchString: string,
  setSearchString: (searchString: string) => void,
  initialData: TreeData[],
  selectedNodeData: TNode[],
  config?: TypeaheadFetchConfig
): {
  data: TreeData[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  inputValue: string;
  setInputValue: (inputValue: string) => void;
  handleChange: ChangeEventHandler<HTMLInputElement>;
  rawMappedResults: TreeData[];
  initialIndustries: TreeData[];
  isSearchTakingTooLong: boolean;
  cancelSearch: () => void;
  isManuallyAborted: boolean;
} => {
  // Config & State
  const { queryKeyPrefix = 'typeahead' } = config || {};
  const [inputValue, setInputValue] = useState<string>('');
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [isManuallyAborted, setIsManuallyAborted] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Cancel search function
  const cancelSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsManuallyAborted(true);
  }, []);

  // Event Handlers
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value;
    setInputValue(value);
    setShowLoader(value.trim().length >= MIN_SEARCH_LENGTH);
  };

  // Debounce search input — only debounce valid searches; clear immediately when too short
  const debouncedInput = useDebounce(inputValue, 200);

  useEffect(() => {
    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      setSearchString(inputValue);
      setShowLoader(false);
      return;
    }
    setSearchString(debouncedInput);
  }, [inputValue, debouncedInput, setSearchString]);

  // Long search timer effect
  useEffect(() => {
    if (searchString && searchString.trim().length >= MIN_SEARCH_LENGTH) {
      abortControllerRef.current = new AbortController();
      setIsManuallyAborted(false);
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchString]);

  // Data Fetching with React Query
  const { data, isError, error, status } = useQuery<FetchResults, Error>({
    queryKey: [queryKeyPrefix, searchString],
    queryFn: ({ signal }) =>
      fetchAndMapResults(searchString, initialData, signal),
    enabled: searchString.trim().length >= MIN_SEARCH_LENGTH,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (status !== 'pending') {
      setShowLoader(false);
    }
  }, [status, searchString]);

  // Extract companies from initial data
  const initialCompanies = useMemo(
    () => extractCompaniesFromTreeData(initialData),
    [initialData]
  );

  // Extract industries from initial data
  const initialIndustries = useMemo(
    () => extractIndustriesFromTreeData(initialData),
    [initialData]
  );

  // Return Hook API
  return {
    data: data?.sortedData || initialData,
    rawMappedResults: data?.rawMappedResults || initialCompanies,
    initialIndustries,
    isLoading: showLoader && !isManuallyAborted,
    isError,
    error,
    inputValue,
    setInputValue,
    handleChange,
    isSearchTakingTooLong: false,
    cancelSearch,
    isManuallyAborted,
  };
};
